/**
 * `<x-lottie>` — loads Bodymovin JSON (SVG via lottie-web) or `.lottie` archives
 * (@lottiefiles/dotlottie-web canvas + WASM).
 */

import type { AnimationItem } from 'lottie-web'
import type { DotLottie } from '@lottiefiles/dotlottie-web'

type LottieHandle =
  | { kind: 'json'; anim: AnimationItem }
  | { kind: 'dotlottie'; player: DotLottie }

let lottiePromise: Promise<typeof import('lottie-web').default> | null = null

function loadLottie () {
  if (!lottiePromise) {
    lottiePromise = import('lottie-web').then((m) => m.default)
  }
  return lottiePromise
}

function isDotLottieUrl (url: string): boolean {
  try {
    const { pathname } = new URL(url)
    return pathname.toLowerCase().endsWith('.lottie')
  } catch {
    const pathOnly = url.split('?')[0] ?? ''
    return pathOnly.toLowerCase().endsWith('.lottie')
  }
}

export class LottieAnimationElement extends HTMLElement {
  private handle?: LottieHandle
  private loadAborted = false

  connectedCallback (): void {
    this.loadAborted = false
    this.bootstrap().catch(() => undefined)
  }

  disconnectedCallback (): void {
    this.loadAborted = true
    this.teardown()
  }

  private teardown (): void {
    const h = this.handle
    this.handle = undefined
    if (h?.kind === 'json') {
      h.anim.destroy()
    } else if (h?.kind === 'dotlottie') {
      h.player.destroy()
    }
    this.replaceChildren()
  }

  private async bootstrap (): Promise<void> {
    const url = this.getAttribute('src')?.trim()
    if (!url) return

    const loop = this.hasAttribute('loop')

    try {
      if (isDotLottieUrl(url)) {
        const { DotLottie } = await import('@lottiefiles/dotlottie-web')
        if (this.loadAborted || !this.isConnected) return

        const canvas = document.createElement('canvas')
        canvas.className = 'block h-auto w-full max-w-full'
        this.appendChild(canvas)

        const player = new DotLottie({
          canvas,
          src: url,
          loop,
          autoplay: true,
          renderConfig: {
            autoResize: true,
            freezeOnOffscreen: true
          },
          layout: {
            fit: 'contain',
            align: [0.5, 0.5]
          }
        })

        this.handle = { kind: 'dotlottie', player }

        if (this.loadAborted || !this.isConnected) {
          this.teardown()
        }
      } else {
        const lottie = await loadLottie()
        if (this.loadAborted || !this.isConnected) return

        const anim = lottie.loadAnimation({
          container: this,
          renderer: 'svg',
          loop,
          autoplay: true,
          path: url
        })

        this.handle = { kind: 'json', anim }

        if (this.loadAborted || !this.isConnected) {
          this.teardown()
        }
      }
    } catch {
      if (!this.loadAborted && this.isConnected) {
        this.replaceChildren()
      }
    }
  }
}
